// localStorage utility for race series app data
interface StoredPointsScheme {
  text: string;
  timestamp: number;
  type: 'preset' | 'custom' | 'natural-language';
}

interface AppStorage {
  pointsSchemes: {
    recent: StoredPointsScheme[];
    lastUsed?: StoredPointsScheme;
  };
}

const STORAGE_KEY = 'raceSeriesApp';
const MAX_RECENT_SCHEMES = 10;

export class LocalStorageManager {
  private static getStorage(): AppStorage {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to parse localStorage data:', error);
    }
    
    return {
      pointsSchemes: {
        recent: []
      }
    };
  }

  private static setStorage(data: AppStorage): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }

  // Points Schemes Management
  static savePointsScheme(text: string, type: 'preset' | 'custom' | 'natural-language'): void {
    if (!text.trim()) return;

    const storage = this.getStorage();
    const scheme: StoredPointsScheme = {
      text: text.trim(),
      timestamp: Date.now(),
      type
    };

    // Update last used
    storage.pointsSchemes.lastUsed = scheme;

    // Add to recent (remove duplicates first)
    storage.pointsSchemes.recent = storage.pointsSchemes.recent.filter(
      s => s.text !== scheme.text
    );
    storage.pointsSchemes.recent.unshift(scheme);

    // Limit recent schemes
    if (storage.pointsSchemes.recent.length > MAX_RECENT_SCHEMES) {
      storage.pointsSchemes.recent = storage.pointsSchemes.recent.slice(0, MAX_RECENT_SCHEMES);
    }

    this.setStorage(storage);
  }

  static getLastUsedPointsScheme(): StoredPointsScheme | null {
    const storage = this.getStorage();
    return storage.pointsSchemes.lastUsed || null;
  }

  static getRecentPointsSchemes(): StoredPointsScheme[] {
    const storage = this.getStorage();
    return storage.pointsSchemes.recent || [];
  }



  // Clear all stored data
  static clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
} 